import { AdvertisementResponse } from '../../../api'
import { axiosInstance } from '../../../services/Services'
import { BulkPassRequest } from '../../../services/queries/useBulkCreatePasses'
import { buildAdFromRow } from './adBuilder'
import { CsvRow, generateCsvWithStatus } from './csvUtils'
import { refreshAuthToken, setupTokenRefresh } from './tokenRefresh'

const BATCH_SIZE = 10
const PARALLEL_BATCHES = 3

interface RowStatus {
  index: number
  status: 'success' | 'failed'
  error?: string
  socialLink?: string
  ctvUrl?: string
}

interface ProcessBatchesParams {
  csvData: CsvRow[]
  brandId: string
  imageUploadCache: Map<string, string>
  getAdDetails: (adId: string) => Promise<AdvertisementResponse>
  onProgress: (progress: number) => void
  onSubmittedCount: (count: number) => void
  csvFileName?: string
}

/**
 * Processes CSV data in batches and creates ads
 */
export const processBatches = async ({
  csvData,
  brandId,
  imageUploadCache,
  getAdDetails,
  onProgress,
  onSubmittedCount,
  csvFileName,
}: ProcessBatchesParams): Promise<{ uploadedCount: number; failedCount: number; rowStatuses: RowStatus[] }> => {
  const totalBatches = Math.ceil(csvData.length / BATCH_SIZE)
  let uploadedCount = 0
  let failedCount = 0
  const rowStatuses: RowStatus[] = []

  // Initialize progress
  onProgress(0)
  onSubmittedCount(0)

  // Set up token refresh to keep session alive during long processing
  const cleanupTokenRefresh = setupTokenRefresh()

  const processBatchGroup = async (batchIndices: number[]) => {
    const batchGroupPromises = batchIndices.map(async i => {
      const batch = csvData.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE)
      const batchStartIndex = i * BATCH_SIZE

      const rowPromises = batch.map(async (row, batchIndex) => {
        const globalIndex = batchStartIndex + batchIndex
        try {
          const rowAdvertId = row.AdvertId || row.advertId

          // Fetch ad details using promise-based cache only if an AdvertId is provided
          let rowAdDetails: AdvertisementResponse | null = null
          if (rowAdvertId) {
            rowAdDetails = await getAdDetails(rowAdvertId)
          }

          const ad = buildAdFromRow({
            row,
            rowAdDetails,
            globalIndex,
            imageUploadCache,
          })

          return {
            ad,
            index: globalIndex,
            row, // Include row data to access clickThroughUrl for CTV URL
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error'
          rowStatuses.push({
            index: globalIndex,
            status: 'failed',
            error: errorMessage,
            socialLink: '',
            ctvUrl: '',
          })
          if (process.env.NODE_ENV === 'development') {
            console.error('Failed to process row:', err)
          }
          return null
        }
      })

      const results = await Promise.allSettled(rowPromises)

      // Count failures from this batch
      const batchFailures = results.filter(
        r => r.status === 'rejected' || (r.status === 'fulfilled' && r.value === null)
      ).length
      failedCount += batchFailures

      const validResults = results
        .filter(r => r.status === 'fulfilled' && r.value !== null)
        .map(r => (r as PromiseFulfilledResult<{ ad: BulkPassRequest; index: number; row: CsvRow } | null>).value)
        .filter((r): r is { ad: BulkPassRequest; index: number; row: CsvRow } => r !== null)

      // Update progress after processing batch
      onSubmittedCount(uploadedCount + failedCount)
      onProgress(Math.round(((uploadedCount + failedCount) / csvData.length) * 100))

      if (validResults.length > 0) {
        try {
          // Send ads wrapped in an object with brandId
          const ads = validResults.map(({ ad }) => ad)

          // In development, use /admin/self-served to leverage proxy (which rewrites to /adminapi/self-served)
          // In production, use full URL
          const endpoint = `${process.env.REACT_APP_BASE_URL || ''}/self-served/ads/bulk`

          // Add retry logic for timeout errors
          const maxRetries = 3

          const attemptRequest = async (
            attemptNumber: number
          ): Promise<{
            apiUploadedCount: number
            apiFailedCount: number
            successfulCreations?: Array<{
              id: string
              socialUrl?: string
            }>
          }> => {
            try {
              // Refresh token proactively before each API call to prevent expiration
              await refreshAuthToken()

              const response = await axiosInstance.post(
                endpoint,
                { brandId, ads },
                {
                  headers: { 'Content-Type': 'application/json' },
                  timeout: 300000, // 5 minute timeout for bulk operations
                }
              )

              const responseData = response?.data as any

              const apiUploadedCount = typeof responseData?.successCount === 'number' ? responseData.successCount : 0

              const apiFailedCount = typeof responseData?.failureCount === 'number' ? responseData.failureCount : 0

              const successfulCreations = Array.isArray(responseData?.successfulCreations)
                ? responseData.successfulCreations
                : undefined

              if (process.env.NODE_ENV === 'development') {
                console.log('API Response:', {
                  successCount: responseData?.successCount,
                  failureCount: responseData?.failureCount,
                  successfulCreations: responseData?.successfulCreations,
                  parsedUploaded: apiUploadedCount,
                  parsedFailed: apiFailedCount,
                })
              }

              return {
                apiUploadedCount,
                apiFailedCount,
                successfulCreations,
              }
            } catch (error: unknown) {
              // Type guard for axios errors
              const axiosError = error as {
                code?: string
                message?: string
                response?: { status?: number }
              }
              const isTimeout =
                axiosError?.code === 'ECONNABORTED' ||
                axiosError?.message?.includes('timeout') ||
                axiosError?.response?.status === 504

              if (isTimeout && attemptNumber < maxRetries) {
                // Wait before retrying (exponential backoff)
                const delay = 1000 * attemptNumber
                await new Promise<void>(resolve => {
                  setTimeout(() => {
                    resolve()
                  }, delay)
                })
                return attemptRequest(attemptNumber + 1)
              }
              throw error // Re-throw if not timeout or last retry
            }
          }

          const apiResponse = await attemptRequest(1)

          // Use API response to determine actual success/failure counts
          // Match successfulCreations with validResults by index (order should match)
          if (apiResponse.successfulCreations && apiResponse.successfulCreations.length > 0) {
            // API provides successfulCreations array - match by position
            validResults.forEach(({ index, row }, idx) => {
              const createdAd = apiResponse.successfulCreations?.[idx]

              if (createdAd) {
                // Extract socialUrl and id from the created ad
                const socialLink = createdAd.socialUrl || ''
                const adId = createdAd.id || ''

                // Get clickThroughUrl from CSV row
                const clickThroughUrl = row.ClickthroughUrl || row.clickthroughUrl || ''

                // Calculate CTV URL using clickThroughUrl from CSV row
                const ctvUrl =
                  adId && clickThroughUrl
                    ? `${
                        process.env.REACT_APP_AD_PACKAGE_REDIRECTION_URL || ''
                      }/auto-pass-generation/${adId}?returnUrl=${encodeURIComponent(clickThroughUrl)}`
                    : ''

                rowStatuses.push({
                  index,
                  status: 'success',
                  socialLink,
                  ctvUrl,
                })
                uploadedCount += 1
              } else {
                // This ad wasn't in successfulCreations, so it failed
                rowStatuses.push({
                  index,
                  status: 'failed',
                  error: 'Ad creation failed',
                  socialLink: '',
                  ctvUrl: '',
                })
                failedCount += 1
              }
            })
          } else {
            // API only provides counts - distribute failures based on uploaded count
            const actualUploaded = Math.max(0, Math.min(apiResponse.apiUploadedCount, validResults.length))

            validResults.forEach(({ index }, idx) => {
              if (idx < actualUploaded) {
                rowStatuses.push({
                  index,
                  status: 'success',
                  socialLink: '',
                  ctvUrl: '',
                })
                uploadedCount += 1
              } else {
                rowStatuses.push({
                  index,
                  status: 'failed',
                  error: 'Ad creation failed (API reported failure)',
                  socialLink: '',
                  ctvUrl: '',
                })
                failedCount += 1
              }
            })
          }

          // Update progress after API response is processed
          onSubmittedCount(uploadedCount + failedCount)
          onProgress(Math.round(((uploadedCount + failedCount) / csvData.length) * 100))
        } catch (error) {
          // Mark failed rows
          validResults.forEach(({ index }) => {
            rowStatuses.push({
              index,
              status: 'failed',
              error: error instanceof Error ? error.message : 'Batch creation failed',
              socialLink: '',
              ctvUrl: '',
            })
          })
          failedCount += validResults.length
          // Update progress even on error
          onSubmittedCount(uploadedCount + failedCount)
          onProgress(Math.round(((uploadedCount + failedCount) / csvData.length) * 100))
          if (process.env.NODE_ENV === 'development') {
            console.error('Batch creation error:', error)
          }
          // Continue with next batch even if this one fails
        }
      }
    })

    // Process all batches in this group in parallel
    await Promise.all(batchGroupPromises)
  }

  // Process batches in groups of PARALLEL_BATCHES (3 batches at a time)
  // Create all batch groups first
  const batchGroups: number[][] = []
  for (let groupStart = 0; groupStart < totalBatches; groupStart += PARALLEL_BATCHES) {
    const batchIndices: number[] = []
    for (let j = 0; j < PARALLEL_BATCHES && groupStart + j < totalBatches; j += 1) {
      batchIndices.push(groupStart + j)
    }
    batchGroups.push(batchIndices)
  }

  try {
    // Process groups sequentially, but batches within each group in parallel
    // Add a small delay between batch groups to avoid overwhelming the database
    await batchGroups.reduce(async (previousPromise, batchIndices, index) => {
      await previousPromise

      // Add delay between batch groups (except for the first one)
      if (index > 0) {
        await new Promise<void>(resolve => {
          setTimeout(() => {
            resolve()
          }, 500) // 500ms delay between batch groups
        })
      }

      return processBatchGroup(batchIndices)
    }, Promise.resolve())

    // Generate and download CSV with status
    generateCsvWithStatus(csvData, rowStatuses, csvFileName)
  } finally {
    // Always clean up token refresh interval, even if there's an error
    cleanupTokenRefresh()
  }

  return { uploadedCount, failedCount, rowStatuses }
}

export { BATCH_SIZE, PARALLEL_BATCHES }
